/**
 * The pen gate at her bay, as a leaf that can swing.
 *
 * Beats 1 and 2 both open it: in beat 1 the worker opens it to take her
 * littermate (beat 1 revision 2, C), and in beat 2 the latch gives. Until
 * the first of those, beat 2 was the only beat that moved it and the swap
 * lived in its part.
 */

import * as THREE from 'three';
import { penGateAsset, penGateLeaf, type PenGateLeaf } from '@/models/env/pen-gate';
import { disposeAsset } from '@/models/materials';
import { HALL, penGateMouths } from '@/world/farrowing';
import { instanceGroup } from '@/world/instancing';

export interface PenGateHinge {
  hinge: { x: number; z: number };
  swing: -1 | 1;
}

/** Where the gate at her bay hangs, and which way it swings. One mouth only. */
export function penGateHinge(): PenGateHinge {
  const mouth = penGateMouths()[0];
  if (!mouth) throw new Error('the farrowing hall declares no pen gate mouth');
  return {
    hinge: { x: mouth.side * (HALL.walkway + 0.06), z: mouth.z + mouth.swing * mouth.width / 2 },
    swing: mouth.swing,
  };
}

export interface LivePenGate extends PenGateHinge {
  mesh: THREE.Object3D;
  /** Pose the mesh at `open` (0 shut, 1 open) and return the leaf, for a collider. */
  set(open: number): PenGateLeaf;
}

/**
 * Take the gate out of the hall's merged dressing and re-instance it as a
 * single leaf that can swing.
 *
 * `instanceGroup` MERGES its placements into world-space geometry, so the
 * `pen-gates` group the dressing builds has an identity transform and a leaf
 * already sitting at (-0.76, -8.8). Setting `rotation.y` on that group would
 * swing the gate about the world origin, twenty metres away: the gate flying
 * across the hall. So the gate is re-instanced as one part at the local
 * origin, whose group transform IS the leaf transform. Net zero draw calls:
 * one merged group out, one merged group in.
 */
export function takeLivePenGate(scene: THREE.Object3D): LivePenGate {
  const { hinge, swing } = penGateHinge();
  const dressed = scene.getObjectByName('pen-gates');
  if (dressed) {
    dressed.removeFromParent();
    disposeAsset(dressed);
  }
  const mesh = instanceGroup(penGateAsset.create(), [{ x: 0, z: 0 }], 'pen-gate-live');
  scene.add(mesh);
  const set = (open: number): PenGateLeaf => {
    const leaf = penGateLeaf(hinge, swing, open);
    mesh.position.set(leaf.pos.x, 0, leaf.pos.z);
    mesh.rotation.y = leaf.rotY;
    return leaf;
  };
  set(0);
  return { hinge, swing, mesh, set };
}
